import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { db, auth, logout } from "./firebase";
import { useAuthState } from "react-firebase-hooks/auth";
import { query, collection, getDocs, where } from "firebase/firestore";
import { Navbar, Nav, Container } from "react-bootstrap";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  BuildingUp,
  CartCheck,
  HouseUp,
  PencilSquare,
  PlusCircle,
  PersonFill,
  CardChecklist,
  BagFill,
  ClockFill,
  GearFill,
  BoxArrowRight,
} from "react-bootstrap-icons";
import { getHost } from "../helpers/getHost";
import ThemeToggle from "../components/reusable/ThemeToggle";
import Avatar from "../components/reusable/Avatar";

const Navigation = () => {
  const [user, loading] = useAuthState(auth);
  const [customerData, setCustomerData] = useState([]);
  const [name, setName] = useState("");
  const [showProfile, setShowProfile] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const profileRef = useRef(null);

  useEffect(() => {
    const getUserName = async () => {
      try {
        const q = query(collection(db, "users"), where("uid", "==", user?.uid));
        const doc = await getDocs(q);
        const data = doc.docs[0]?.data();
        if (data) setName(data.name);
      } catch (err) {
        console.log(err);
      }
    };

    if (!loading && user) {
      getUserName();
    }
  }, [loading, user]);

  useEffect(() => {
    if (!user) return;

    const getCustomer = async () => {
      try {
        const res = await axios.get(
          `${getHost()}/api/signup?userEmail=${user.email}`,
          {
            headers: {
              Authorization: `Bearer ${user.accessToken}`,
            },
          }
        );
        if (res.status === 200) {
          setCustomerData(res.data);
        }
      } catch (err) {
        console.warn(err.response);
      }
    };

    getCustomer();
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setShowProfile(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const navLinks = [
    { to: "/home", icon: HouseUp, label: "Home" },
    { to: "/register", icon: PencilSquare, label: "Add Business" },
    { to: "/vendors", icon: CartCheck, label: "Vendors" },
    { to: "/connect", icon: PlusCircle, label: "Connect" },
    { to: "/categories", icon: CardChecklist, label: "Categories" },
    { to: "/admin", icon: BuildingUp, label: "My Business" },
  ];

  const displayName = customerData[0]?.userName ?? name;

  return (
    <Navbar
      collapseOnSelect
      expand="lg"
      className="main-nav fixed-top px-0"
    >
      <Container fluid className="px-4">
        <Navbar.Brand
          as={Link}
          to="/home"
          className="fw-bold d-flex align-items-center gap-2"
          style={{ color: "var(--accent)", fontSize: "22px", letterSpacing: "-0.5px" }}
        >
          <BagFill /> dugam
        </Navbar.Brand>

        <Navbar.Toggle
          aria-controls="main-nav"
          style={{ border: "none", color: "var(--text-primary)" }}
        />

        <Navbar.Collapse id="main-nav">
          <Nav className="me-auto d-flex align-items-start flex-column flex-lg-row gap-1 mt-3 mt-lg-0">
            {navLinks.map(({ to, icon: Icon, label }) => (
              <Nav.Link
                key={to}
                as={Link}
                to={to}
                className={`nav-link-modern ${
                  location.pathname === to ? "active" : ""
                }`}
              >
                <Icon /> <small>{label}</small>
              </Nav.Link>
            ))}
          </Nav>

          <Nav className="ms-lg-auto mt-3 mt-lg-0 d-flex align-items-center gap-2">
            <ThemeToggle />

            {/* Custom profile button — no caret */}
            <div ref={profileRef} style={{ position: "relative" }}>
              <button
                className="d-flex align-items-center gap-2 border-0 px-2 py-1"
                onClick={() => setShowProfile((prev) => !prev)}
                style={{
                  background: showProfile ? "var(--bg-hover)" : "transparent",
                  borderRadius: "var(--radius-full)",
                  cursor: "pointer",
                  outline: "none",
                }}
              >
                <Avatar size="sm" />
              </button>

              {/* Dropdown panel */}
              {showProfile && (
                <div
                  style={{
                    position: "absolute",
                    top: "calc(100% + 8px)",
                    right: 0,
                    minWidth: "280px",
                    borderRadius: "var(--radius-lg)",
                    overflow: "hidden",
                    zIndex: 2000,
                    background: "rgba(255, 255, 255, 0.15)",
                    backdropFilter: "blur(50px) saturate(190%)",
                    WebkitBackdropFilter: "blur(50px) saturate(190%)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    boxShadow: "0 12px 40px rgba(0,0,0,0.08), inset 0 0.5px 0 rgba(255,255,255,0.3)",
                  }}
                  className="profile-dropdown-glass"
                >
                  {/* User info card */}
                  <div
                    className="d-flex align-items-center gap-3 p-3"
                    style={{
                      cursor: "pointer",
                      borderBottom: "1px solid rgba(255,255,255,0.2)",
                    }}
                    onClick={() => {
                      navigate("/admin");
                      setShowProfile(false);
                    }}
                  >
                    <div
                      style={{
                        width: "48px",
                        height: "48px",
                        borderRadius: "var(--radius-full)",
                        background: "linear-gradient(135deg, var(--accent), var(--accent-secondary))",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        fontSize: "20px",
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {displayName ? displayName.charAt(0).toUpperCase() : <PersonFill />}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div
                        className="fw-bold text-truncate"
                        style={{ fontSize: "15px", color: "var(--text-primary)" }}
                      >
                        {displayName || "User"}
                      </div>
                      <div
                        className="text-truncate"
                        style={{ fontSize: "12px", color: "var(--text-secondary)" }}
                      >
                        {user?.email}
                      </div>
                    </div>
                  </div>

                  {/* Meta */}
                  <div
                    className="px-3 py-2 d-flex align-items-center gap-2"
                    style={{
                      fontSize: "12px",
                      color: "var(--text-tertiary)",
                      borderBottom: "1px solid rgba(255,255,255,0.15)",
                    }}
                  >
                    <ClockFill style={{ fontSize: "11px" }} />
                    Last login: {user?.metadata?.lastSignInTime?.split(",")[0] ?? "—"}
                  </div>

                  {/* Actions */}
                  <div className="py-1">
                    <button
                      className="popover-action w-100"
                      onClick={() => {
                        navigate("/admin");
                        setShowProfile(false);
                      }}
                    >
                      <GearFill /> My Business
                    </button>
                    <button
                      className="popover-action w-100"
                      style={{ color: "var(--like-color)" }}
                      onClick={() => {
                        logout();
                        navigate("/");
                        setShowProfile(false);
                      }}
                    >
                      <BoxArrowRight /> Log out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

export default Navigation;
